import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  COMPANY_GSTIN,
  computeInvoiceTotals,
  computeLine,
  formatProductName,
  getInvoiceStateLabel,
  numberToWordsINR
} from './zovcurInvoiceStore';

const NAVY = [14, 61, 82];
const NAVY_DEEP = [7, 38, 54];
const TEAL = [14, 138, 154];
const AMBER = [232, 140, 60];
const SLATE = [91, 107, 124];
const INK = [30, 41, 59];
const LIGHT = [244, 247, 250];
const WHITE = [255, 255, 255];
const BORDER = [226, 232, 240];

const loadImageBase64 = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = reject;
    img.src = src;
  });

const fmt = (n) =>
  (Number(n) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const drawSectionBar = (doc, x, y, w, title) => {
  doc.setFillColor(...NAVY);
  doc.roundedRect(x, y, w, 7, 1.5, 1.5, 'F');
  doc.setFillColor(...TEAL);
  doc.rect(x, y, 2.2, 7, 'F');
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(title, x + 5, y + 4.7);
};

const labelValue = (doc, x, y, label, value, { labelW = 24, valueMaxW = 60 } = {}) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  doc.text(`${label}`, x, y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  const text = String(value || '').trim() || '—';
  const lines = doc.splitTextToSize(text, valueMaxW);
  doc.text(lines, x + labelW, y);
  return y + Math.max(lines.length * 3.6, 4);
};

export const buildZovcurInvoicePdf = async (invoice, logoSrc) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  let y = 0;

  const totals = computeInvoiceTotals(invoice.products);
  const amountWords = numberToWordsINR(totals.invoiceAmount);
  const billNo = invoice.invoiceDetails?.billNo || '—';
  const invoiceNo = invoice.invoiceNumber || '—';
  const fileBillNo = invoice.invoiceDetails?.billNo || invoice.invoiceNumber || 'draft';

  // ── Header ──────────────────────────────────────────────
  doc.setFillColor(...NAVY_DEEP);
  doc.rect(0, 0, pageW, 36, 'F');

  // Accent stripe under header
  doc.setFillColor(...TEAL);
  doc.rect(0, 36, pageW * 0.55, 2.2, 'F');
  doc.setFillColor(...AMBER);
  doc.rect(pageW * 0.55, 36, pageW * 0.25, 2.2, 'F');
  doc.setFillColor(224, 107, 107);
  doc.rect(pageW * 0.8, 36, pageW * 0.2, 2.2, 'F');

  // Logo on white plate
  try {
    const logoData = await loadImageBase64(logoSrc);
    doc.setFillColor(...WHITE);
    doc.roundedRect(margin, 6, 30, 22, 2, 2, 'F');
    doc.addImage(logoData, 'JPEG', margin + 2, 7.5, 26, 19);
  } catch {
    doc.setFillColor(...WHITE);
    doc.roundedRect(margin, 6, 30, 22, 2, 2, 'F');
    doc.setTextColor(...NAVY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Zovcur', margin + 6, 19);
  }

  const leftX = margin + 34;
  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(invoice.company?.name || 'Zovcur Pharma', leftX, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(186, 230, 235);
  const addrLines = doc.splitTextToSize(invoice.company?.address || '', pageW - leftX - 48);
  doc.text(addrLines, leftX, 18.5);

  doc.setFontSize(7);
  doc.setTextColor(...WHITE);
  doc.text(`DL No: ${invoice.company?.dlNo || '—'}`, leftX, 31);
  doc.text(`Phone: ${invoice.company?.phone || '—'}`, leftX + 48, 31);

  // Tax invoice badge + bill no
  doc.setFillColor(...AMBER);
  doc.roundedRect(pageW - margin - 40, 7, 40, 11, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...WHITE);
  doc.text('TAX INVOICE', pageW - margin - 20, 14, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(186, 230, 235);
  doc.text(`Invoice: ${invoiceNo}`, pageW - margin - 20, 24, { align: 'center' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text(`Bill: ${billNo}`, pageW - margin - 20, 29, { align: 'center' });

  y = 44;

  // ── Customer + Invoice cards ─────────────────────────────
  const colW = (pageW - margin * 2 - 4) / 2;
  const boxH = 58;
  const labelW = 22;
  const valueMaxW = colW - labelW - 8;
  const invoiceLabelW = 24;
  const invoiceValueMaxW = colW - invoiceLabelW - 8;

  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.35);
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, y, colW, boxH, 2, 2, 'FD');
  doc.roundedRect(margin + colW + 4, y, colW, boxH, 2, 2, 'FD');

  drawSectionBar(doc, margin, y, colW, 'CUSTOMER DETAILS');
  drawSectionBar(doc, margin + colW + 4, y, colW, 'INVOICE DETAILS');

  let cy = y + 12;
  const cx = margin + 3;
  const customerOpts = { labelW, valueMaxW };
  cy = labelValue(doc, cx, cy, 'Doctor', invoice.customer?.doctor, customerOpts);
  cy = labelValue(doc, cx, cy, 'Address', invoice.customer?.address, customerOpts);
  cy = labelValue(doc, cx, cy, 'Phone', invoice.customer?.phone, customerOpts);
  labelValue(doc, cx, cy, 'State', getInvoiceStateLabel(invoice), customerOpts);

  let iy = y + 12;
  const ix = margin + colW + 7;
  const invoiceOpts = { labelW: invoiceLabelW, valueMaxW: invoiceValueMaxW };
  iy = labelValue(doc, ix, iy, 'Bill Type', invoice.invoiceDetails?.billType, invoiceOpts);
  iy = labelValue(doc, ix, iy, 'Bill No', billNo, invoiceOpts);
  iy = labelValue(doc, ix, iy, 'Bill Date', fmtDate(invoice.invoiceDetails?.billDate), invoiceOpts);
  iy = labelValue(doc, ix, iy, 'Order Date', fmtDate(invoice.invoiceDetails?.orderDate), invoiceOpts);
  iy = labelValue(doc, ix, iy, 'Due Date', fmtDate(invoice.invoiceDetails?.dueDate), invoiceOpts);
  iy = labelValue(doc, ix, iy, 'Salesman', invoice.invoiceDetails?.salesmanName, invoiceOpts);
  labelValue(doc, ix, iy, 'GSTIN', invoice.invoiceDetails?.gstin || COMPANY_GSTIN, invoiceOpts);

  y += boxH + 4;

  // Meta chips row
  doc.setFillColor(236, 253, 245);
  doc.roundedRect(margin, y, pageW - margin * 2, 8, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...NAVY);
  const meta = [
    `State Code: ${invoice.invoiceDetails?.stateCode || '33'}`,
    `State: ${invoice.invoiceDetails?.stateName || 'Tamil Nadu'}`,
    `REG No: ${invoice.invoiceDetails?.regNo || '—'}`,
    `Delivery: ${invoice.invoiceDetails?.deliveryType || 'Picklist'}`,
    `Page: ${invoice.invoiceDetails?.pageNo || '1/1'}`
  ].join('   ·   ');
  doc.text(meta, margin + 3, y + 5.2);
  y += 12;

  // ── Products table ──────────────────────────────────────
  const productRows = invoice.products
    .filter((p) => p.product || Number(p.qty))
    .map((p, i) => {
      const line = computeLine(p);
      return [
        i + 1,
        p.product ? formatProductName(p.product) : '—',
        p.batchNo || '—',
        p.exp || '—',
        p.qty || '0',
        fmt(p.rate),
        fmt(p.mrp),
        `${p.gstPercent || 0}%`,
        fmt(line.total)
      ];
    });

  // A4 content width = 210 - 20 margin = 190mm
  const tableW = pageW - margin * 2;
  autoTable(doc, {
    startY: y,
    head: [['#', 'PRODUCT', 'BATCH', 'EXP', 'QTY', 'RATE', 'MRP', 'GST', 'TOTAL']],
    body: productRows.length ? productRows : [['—', '—', '—', '—', '—', '—', '—', '—', '—']],
    margin: { left: margin, right: margin },
    tableWidth: tableW,
    styles: {
      fontSize: 7.5,
      cellPadding: 2.2,
      textColor: INK,
      lineColor: BORDER,
      lineWidth: 0.2,
      valign: 'middle',
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: NAVY,
      textColor: WHITE,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 7,
      cellPadding: 2.4
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { fontStyle: 'bold', halign: 'left', cellWidth: 52 },
      2: { halign: 'center', cellWidth: 24 },
      3: { halign: 'center', cellWidth: 16 },
      4: { halign: 'center', cellWidth: 14 },
      5: { halign: 'right', cellWidth: 22 },
      6: { halign: 'right', cellWidth: 22 },
      7: { halign: 'center', cellWidth: 14 },
      8: { halign: 'right', fontStyle: 'bold', textColor: NAVY, cellWidth: 16 }
    }
  });

  y = doc.lastAutoTable.finalY + 5;

  // ── Summary + GST ───────────────────────────────────────
  const sumColW = (pageW - margin * 2 - 4) / 2;
  const sumBoxH = 48;

  // Ensure room on page
  if (y + sumBoxH + 45 > pageH - 12) {
    doc.addPage();
    y = margin;
  }

  doc.setDrawColor(...BORDER);
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, y, sumColW, sumBoxH, 2, 2, 'FD');
  doc.roundedRect(margin + sumColW + 4, y, sumColW, sumBoxH, 2, 2, 'FD');

  drawSectionBar(doc, margin, y, sumColW, 'SUMMARY');
  drawSectionBar(doc, margin + sumColW + 4, y, sumColW, 'GST SUMMARY');

  const summaryLines = [
    ['Items', String(totals.items)],
    ['Quantity (strips)', String(totals.quantity)],
    ['Base Amount', `Rs. ${fmt(totals.baseAmount)}`],
    ['SGST', `Rs. ${fmt(totals.sgst)}`],
    ['CGST', `Rs. ${fmt(totals.cgst)}`],
    ['GST Total', `Rs. ${fmt(totals.gstTotal)}`],
    ['Invoice Amount', `Rs. ${fmt(totals.invoiceAmount)}`]
  ];

  let sy = y + 12;
  summaryLines.forEach(([label, val], idx) => {
    const isTotal = idx === summaryLines.length - 1;
    if (isTotal) {
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.3);
      doc.line(margin + 3, sy - 2.5, margin + sumColW - 3, sy - 2.5);
    }
    doc.setFont('helvetica', isTotal ? 'bold' : 'normal');
    doc.setFontSize(isTotal ? 9 : 7.5);
    doc.setTextColor(...(isTotal ? NAVY : SLATE));
    doc.text(label, margin + 3, sy);
    doc.setTextColor(...(isTotal ? NAVY : INK));
    doc.text(String(val), margin + sumColW - 4, sy, { align: 'right' });
    sy += isTotal ? 5.5 : 4.3;
  });

  const gstRows = Object.entries(totals.gstBuckets)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([pct, b]) => [`GST ${pct}%`, fmt(b.base), fmt(b.gst), fmt(b.amount)]);

  autoTable(doc, {
    startY: y + 10,
    margin: { left: margin + sumColW + 4, right: margin },
    tableWidth: sumColW - 2,
    head: [['Category', 'Base', 'GST', 'Amount']],
    body: gstRows.length ? gstRows : [['—', '—', '—', '—']],
    styles: { fontSize: 7, cellPadding: 1.6, textColor: INK, lineColor: BORDER, lineWidth: 0.15 },
    headStyles: { fillColor: TEAL, textColor: WHITE, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right', fontStyle: 'bold' }
    }
  });

  y += sumBoxH + 5;

  // ── Amount in words ─────────────────────────────────────
  if (y + 18 > pageH - 40) {
    doc.addPage();
    y = margin;
  }

  doc.setFillColor(255, 251, 235);
  doc.setDrawColor(253, 230, 138);
  doc.roundedRect(margin, y, pageW - margin * 2, 14, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...SLATE);
  doc.text('AMOUNT IN WORDS', margin + 3, y + 4.5);
  doc.setFont('helvetica', 'bolditalic');
  doc.setFontSize(8.5);
  doc.setTextColor(...NAVY);
  const wordLines = doc.splitTextToSize(amountWords, pageW - margin * 2 - 6);
  doc.text(wordLines, margin + 3, y + 9.5);
  y += 18;

  // ── Bank details ────────────────────────────────────────
  if (y + 24 > pageH - 18) {
    doc.addPage();
    y = margin;
  }

  doc.setDrawColor(...BORDER);
  doc.setFillColor(...LIGHT);
  doc.roundedRect(margin, y, pageW - margin * 2, 22, 2, 2, 'FD');
  drawSectionBar(doc, margin, y, pageW - margin * 2, 'BANK DETAILS');

  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const bankY = y + 12;
  doc.setTextColor(...SLATE);
  doc.text('Bank', margin + 3, bankY);
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.bankDetails?.bank || '—', margin + 18, bankY);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('A/C No', margin + 95, bankY);
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.bankDetails?.acNo || '—', margin + 112, bankY);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('IFSC', margin + 3, bankY + 6);
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  doc.text(invoice.bankDetails?.ifsc || '—', margin + 18, bankY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('Branch', margin + 95, bankY + 6);
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'bold');
  const branchLines = doc.splitTextToSize(invoice.bankDetails?.branch || '—', 70);
  doc.text(branchLines, margin + 112, bankY + 6);

  // ── Footer on all pages ─────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFillColor(...NAVY);
    doc.rect(0, pageH - 10, pageW, 10, 'F');
    doc.setFillColor(...TEAL);
    doc.rect(0, pageH - 10, pageW, 1.2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(186, 230, 235);
    doc.text(
      'Computer generated tax invoice · Zovcur Pharma Pvt Ltd',
      margin,
      pageH - 4
    );
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 4, { align: 'right' });
  }

  return { doc, fileBillNo };
};

export const getZovcurInvoicePdfBlobUrl = async (invoice, logoSrc) => {
  const { doc } = await buildZovcurInvoicePdf(invoice, logoSrc);
  return doc.output('bloburl');
};

export const generateZovcurInvoicePdf = async (invoice, logoSrc) => {
  const { doc, fileBillNo } = await buildZovcurInvoicePdf(invoice, logoSrc);
  doc.save(`Zovcur_Invoice_${fileBillNo}.pdf`);
};
